import React, { useCallback, useEffect, useState } from 'react';
import {
  inputAdornmentClasses,
  inputBaseClasses,
  styled,
  type SxProps,
  type Theme,
  touchRippleClasses,
  typographyClasses
} from '@mui/material';
import { mergeSx } from 'merge-sx';
import { classes, MuiTelInput, type MuiTelInputCountry } from 'mui-tel-input';
import { useDebounceValue } from 'usehooks-ts';

import ChevronDown from '@/assets/icons/chevron-down.svg';
import withFormControl from '@/hoc/withFormControl';
import { borders } from '@/theme/constants';
import shadows from '@/theme/shadows';

const EXCLUDED_COUNTRIES: MuiTelInputCountry[] = ['PS', 'KP'];

interface PhoneInputProps {
  id?: string;
  name?: string;
  value?: string;
  error?: string;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  defaultCountry?: MuiTelInputCountry;
  onChange?: (value: string) => void;
}

const { flagButton, flagContainer, flagImg } = classes;

const PHONE_INPUT_DEBOUNCE_DELAY = 300;

const PhoneInputField = ({ error, defaultCountry = 'US', onChange, ...props }: PhoneInputProps) => {
  const [value, setValue] = useState<string>(props.value || '');

  const [debouncedValue] = useDebounceValue(value, PHONE_INPUT_DEBOUNCE_DELAY);

  useEffect(() => {
    onChange?.(debouncedValue);
  }, [debouncedValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback((newValue: string) => {
    setValue(newValue || '');
  }, []);

  return (
    <CustomMuiTelInput
      {...props}
      sx={mergeSx(props.sx, {
        ...(props.disabled && {
          '& .MuiInputBase-root': {
            bgcolor: 'grey.200',
            color: 'grey.500'
          }
        })
      })}
      onChange={handleChange}
      value={value}
      label=""
      error={!!error}
      defaultCountry={defaultCountry}
      excludedCountries={EXCLUDED_COUNTRIES}
    />
  );
};

const PhoneInput = withFormControl<string, string, PhoneInputProps>(PhoneInputField);

export default PhoneInput;

const CustomMuiTelInput = styled(MuiTelInput)(
  ({ error, value, disabled, theme: { spacing, palette, typography } }) => ({
    direction: 'ltr',

    '& .MuiInputBase-root': {
      backgroundColor: palette.background.default,

      ...(!!value && {
        '& fieldset': {
          borderColor: 'grey.600'
        },
        [`& .${inputAdornmentClasses.positionStart} .${typographyClasses.root}`]: {
          ...(!disabled && {
            color: palette.text.primary
          })
        }
      }),
      ...(!!error && {
        '&:hover fieldset': {
          borderColor: palette.error.main,
          borderWidth: borders._1px
        },
        '& fieldset': {
          borderWidth: borders._1px
        }
      }),
      [`& .${inputBaseClasses.input}`]: {
        paddingLeft: spacing(0.5)
      },

      '&.Mui-focused': {
        boxShadow: shadows[2]
      },

      '& fieldset': {
        borderColor: 'grey.400'
      }
    },

    [`& .${flagButton}`]: {
      justifyContent: 'start',
      width: spacing(4.5),
      height: spacing(2.5),
      padding: 0,

      '&:hover': {
        backgroundColor: 'unset'
      },

      [`.${touchRippleClasses.root}`]: { display: 'none' }
    },

    [`& .${flagButton}::after`]: {
      content: '""',
      width: spacing(2),
      height: spacing(2),
      backgroundImage: `url(${ChevronDown})`,
      opacity: 0.6
    },

    [`& .${flagButton}:focus`]: {
      borderRadius: spacing(0.5),
      boxShadow: shadows[2]
    },

    [`& .${flagContainer}`]: {
      position: 'relative',
      width: spacing(2.5),
      height: spacing(2.5)
    },

    [`& .${flagImg}`]: {
      position: 'absolute',
      left: '50%',
      top: '50%',
      translate: '-50% -50%',
      clipPath: 'circle(40%)'
    },

    [`& .${inputBaseClasses.adornedStart}`]: {
      paddingLeft: 0,
      paddingInlineStart: spacing(1)
    },

    [`& .${inputAdornmentClasses.root}`]: {
      marginRight: 0
    },

    [`& .Mui-disabled .${inputAdornmentClasses.root}`]: {
      color: 'rgba(0, 0, 0, 0.38)'
    },

    [`& .${inputAdornmentClasses.positionStart} .${typographyClasses.root}`]: {
      paddingInlineEnd: 0,
      paddingInlineStart: spacing(1),
      marginInlineStart: spacing(1),
      borderInlineStart: `1px solid ${palette.grey[400]}`,
      borderInlineEnd: 'unset',
      fontFamily: typography.body2.fontFamily,
      fontSize: typography.body2.fontSize,
      lineHeight: spacing(3.75),
      color: 'currentcolor'
    }
  })
);
